// Browser side of the per-tenant document cache (`od-files-<tenant>`, `odx-files-<tenant>` encrypted).
//
// THE PAGE IS THE ONLY WRITER. The service worker serves this cache read-only
// (with Range support) and never stores anything itself — a pdf.js probe that
// gets aborted mid-stream would otherwise leave a truncated body behind.
// Requests carry `x-od-sw-bypass: 1` so the SW passes them straight to the
// network (a changed contentHash must not be answered from the stale cache).
//
// Encrypted mode (secure.js): bodies are stored block-encrypted (odCrypto.js)
// under opaque /od/f/<hmac> keys in `odx-files-<tenant>`; fileState is keyed
// by the same opaque path. Planning (filePlan.js) still works on real paths —
// `pathMap` translates in both directions.
import { openOfflineDb } from "./db";
import { classifyDoc, planFileSync, thumbnailKeys, glbKey } from "./filePlan";
import { isEncrypted, getKeys, storeKey, openRows, cachePrefix } from "./secure.js";
import { encryptStream } from "./odCrypto.js";

export const fileCacheName = (tenant) => `${cachePrefix()}files-${tenant}`;
export const CAD_ASSET_URLS = ["/cad/mtext-renderer-worker.js", "/cad/libredwg-parser-worker.js", "/cad/libredwg-web.wasm"];
const BYPASS_HEADERS = { "x-od-sw-bypass": "1" };
const hasCaches = () => typeof caches !== "undefined";

export async function ensurePersisted() {
  try {
    if (!navigator.storage?.persist) return false;
    if (await navigator.storage.persisted()) return true;
    return await navigator.storage.persist();
  } catch { return false; }
}

export async function listCachedKeys(tenant) {
  if (!hasCaches() || !tenant) return new Set();
  const cache = await caches.open(fileCacheName(tenant));
  const reqs = await cache.keys();
  return new Set(reqs.map((r) => new URL(r.url).pathname));
}

async function readDocs(db) {
  const rows = await db.documents.toArray();
  return isEncrypted() ? openRows("documents", rows) : rows;
}

// Real cache path ↔ stored key for every file of the snapshot (identity in plain mode).
export async function pathMap(docs) {
  const toStore = new Map();
  for (const d of docs || []) {
    const e = classifyDoc(d);
    if (!e) continue;
    for (const k of new Set([e.key, d.path, glbKey(d.path)])) if (k && !toStore.has(k)) toStore.set(k, await storeKey(k));
  }
  const toPath = new Map([...toStore].map(([a, b]) => [b, a]));
  return { toStore, toPath };
}

// → { status: 'stored'|'pending'|'failed'|'auth'|'network'|'aborted', size?, httpStatus? }
export async function fetchAndStore(cache, key, { signal, storedAs = key } = {}) {
  let res;
  try {
    res = await fetch(key, { credentials: "include", redirect: "manual", cache: "no-store", headers: BYPASS_HEADERS, signal });
  } catch (err) {
    return { status: err?.name === "AbortError" ? "aborted" : "network" };
  }
  if (res.type === "opaqueredirect" || res.status === 401 || res.status === 403) return { status: "auth" };
  if (res.status === 202) return { status: "pending" };           // GLB still converting
  if (!res.ok) return { status: "failed", httpStatus: res.status };
  const size = Number(res.headers.get("content-length")) || null;
  try {
    if (isEncrypted()) {
      // Encrypted while streaming into the cache; the AAD binds every block to its opaque key.
      const body = encryptStream(getKeys(), storedAs, res.body);
      const headers = { "content-type": res.headers.get("content-type") || "application/octet-stream" };
      await cache.put(storedAs, new Response(body, { headers }));
    } else {
      await cache.put(key, res);                                   // rejects on a truncated body → nothing stored
    }
  } catch {
    return { status: "failed" };
  }
  return { status: "stored", size };
}

async function findDocForKey(db, key) {
  if (!db) return null;
  const base = key.replace(/\/preview\.glb$/, "");
  try {
    if (isEncrypted()) return (await readDocs(db)).find((d) => d.path === base) || null;
    return await db.documents.filter((d) => d.path === base).first();
  } catch { return null; }
}

// Cache-on-view: called when a viewer opens a document. Skips when the cached
// copy already matches the document's contentHash.
export async function ensureCached({ tenant, url, kind = "doc" }) {
  if (!hasCaches() || !tenant || !url) return null;
  const db = openOfflineDb(tenant);
  const key = kind === "glb" ? glbKey(url) : url;
  const doc = await findDocForKey(db, key);
  const contentHash = doc?.contentHash || null;
  if (isEncrypted() && !getKeys()) return null;
  const stored = await storeKey(key);
  const cache = await caches.open(fileCacheName(tenant));
  const existing = await cache.match(stored);
  const state = db ? await db.fileState.get(stored).catch(() => null) : null;
  if (existing && (!state || (state.contentHash || null) === contentHash)) {
    if (!state && db) {
      await db.fileState.put({ key: stored, docId: doc?.docId || null, kind, contentHash, size: Number(existing.headers.get("content-length")) || null, cachedAt: Date.now(), source: "view" });
    }
    return "cached";
  }
  const r = await fetchAndStore(cache, key, { storedAs: stored });
  if (r.status === "stored" && db) {
    await db.fileState.put({ key: stored, docId: doc?.docId || null, kind, contentHash, size: r.size, cachedAt: Date.now(), source: state?.source || "view" });
  }
  return r.status;
}

async function pool(items, concurrency, fn) {
  const queue = items.slice();
  const workers = Array.from({ length: Math.max(1, concurrency) }, async () => {
    while (queue.length) {
      const item = queue.shift();
      const stop = await fn(item);
      if (stop === false) return;
    }
  });
  await Promise.all(workers);
}

export async function warmCadAssets() {
  if (!hasCaches()) return;
  try {
    const cache = await caches.open("od-cad-v1");
    for (const u of CAD_ASSET_URLS) {
      if (await cache.match(u)) continue;
      const r = await fetch(u, { cache: "no-store" });
      if (r.ok) await cache.put(u, r);
    }
  } catch { /* best effort */ }
}

// Full sync according to `mode`. Resumable by construction: fileState records
// what is cached at which contentHash, so re-running only fetches the delta.
export async function runFullSync({ tenant, db, mode, signal, onProgress, concurrency = 3 }) {
  if (!hasCaches() || !tenant || !db) return { status: "error", error: "cache api unavailable" };
  if (isEncrypted() && !getKeys()) return { status: "error", error: "offline copy locked" };
  const cache = await caches.open(fileCacheName(tenant));
  const docs = await readDocs(db);
  const { toStore, toPath } = await pathMap(docs);
  const asPath = (k) => toPath.get(k) ?? k;                        // unknown stored keys stay as they are → deleted
  const asStored = (k) => toStore.get(k) ?? k;
  const states = new Map((await db.fileState.toArray()).map((s) => [asPath(s.key), s]));
  const cachedKeys = new Set([...(await listCachedKeys(tenant))].map(asPath));
  const plan = planFileSync(docs, { mode, states, cachedKeys });

  const progress = { total: plan.toFetch.length, done: 0, stored: 0, pending: 0, failed: 0, deleted: 0, bytes: 0, current: null };
  const report = () => onProgress?.({ ...progress });
  let stopReason = null;

  for (const key of plan.toDelete) {
    await cache.delete(asStored(key));
    await db.fileState.delete(asStored(key));
    progress.deleted++;
  }
  report();

  await pool(plan.toFetch, concurrency, async (e) => {
    if (stopReason || signal?.aborted) { stopReason ||= "aborted"; return false; }
    progress.current = e.key;
    report();
    const r = await fetchAndStore(cache, e.key, { signal, storedAs: asStored(e.key) });
    if (r.status === "auth") { stopReason = "auth"; return false; }
    if (r.status === "aborted") { stopReason = "aborted"; return false; }
    if (r.status === "stored") {
      progress.stored++;
      progress.bytes += r.size || 0;
      await db.fileState.put({ key: asStored(e.key), docId: e.docId, kind: e.kind, contentHash: e.contentHash, size: r.size, cachedAt: Date.now(), source: "sync" });
    } else if (r.status === "pending") progress.pending++;
    else progress.failed++;
    progress.done++;
    report();
    return true;
  });

  if (!stopReason && mode !== "none") {
    // Thumbnails go through the SW's CacheFirst route → stored by the SW itself.
    await pool(thumbnailKeys(docs), 4, async (k) => {
      if (signal?.aborted) return false;
      try { await fetch(k, { credentials: "include", cache: "no-store", signal }); } catch { /* ignore */ }
      return true;
    });
    if (mode === "docs+cad") await warmCadAssets();
  }
  progress.current = null;
  report();
  return { status: stopReason || "ok", ...progress };
}

export async function evictDocs(tenant, db, docIds) {
  if (!hasCaches() || !db || !docIds?.length) return;
  const rows = await db.fileState.where("docId").anyOf(docIds).toArray();
  const cache = await caches.open(fileCacheName(tenant));
  for (const r of rows) {
    await cache.delete(r.key);
    await db.fileState.delete(r.key);
  }
}

export async function deleteFileCache(tenant, db) {
  if (hasCaches()) {
    await caches.delete(fileCacheName(tenant));
    await caches.delete(`${cachePrefix()}thumbs-${tenant}`);
  }
  if (db) { try { await db.fileState.clear(); } catch { /* db may be gone */ } }
}

export async function cacheStats(db) {
  if (!db) return { count: 0, bytes: 0, keys: new Set() };
  const rows = await db.fileState.toArray();
  // `keys` are real paths (Documents.jsx marks cached documents by doc.path).
  let keys = new Set(rows.map((r) => r.key));
  if (isEncrypted()) {
    if (!getKeys()) keys = new Set();
    else { const { toPath } = await pathMap(await readDocs(db)); keys = new Set(rows.map((r) => toPath.get(r.key)).filter(Boolean)); }
  }
  return { count: rows.length, bytes: rows.reduce((a, r) => a + (r.size || 0), 0), keys };
}

export { classifyDoc };
